import { ArrowRight, Heart, Users, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Custom Instagram icon
const InstagramIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

export const EcosystemSection = () => {
  const levels = [
    {
      level: "01",
      icon: InstagramIcon,
      title: "Instagram",
      subtitle: "Ingresso Emotivo",
      description:
        "Segui @phisioletters per ispirazioni quotidiane, consigli pratici e contenuti che nutrono corpo e mente. Il primo passo verso il tuo benessere.",
      color: "bg-gradient-to-br from-[#e8ece6] to-[#d4c4a8]/30",
      iconBg: "bg-[#8a9a86]/20",
      link: "https://www.instagram.com/phisioletters/",
      linkText: "Segui su Instagram",
      external: true,
    },
    {
      level: "02",
      icon: Users,
      title: "Community",
      subtitle: "Core Business",
      description:
        "Entra nella community su Skool dove troverai video esclusivi, esercizi guidati e il supporto di una comunità che condivide il tuo percorso.",
      color: "bg-gradient-to-br from-[#2c4c3b]/5 to-[#8a9a86]/10",
      iconBg: "bg-[#2c4c3b]/10",
      link: "https://skool.com/phisioletters-9232",
      linkText: "Entra nella Community",
      external: true,
    },
    {
      level: "03",
      icon: Crown,
      title: "Premium",
      subtitle: "Consulenza 1:1",
      description:
        "L'esperienza più esclusiva: consulenze personalizzate, un piano su misura per te e lettere fisiche che ti accompagnano nel tuo percorso.",
      color: "bg-gradient-to-br from-[#d4c4a8]/30 to-[#2c4c3b]/5",
      iconBg: "bg-[#d4c4a8]",
      link: "/pricing",
      linkText: "Scopri Premium",
      external: false,
      featured: true,
    },
  ];

  return (
    <section
      id="ecosistema"
      className="py-24 md:py-32 bg-stone-100"
      data-testid="ecosystem-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
            Il Metodo
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-tight leading-tight mt-4 mb-6">
            Un ecosistema unico
          </h2>
          <p className="text-stone-600 leading-relaxed">
            Tre livelli progressivi pensati per accompagnarti nel tuo percorso di benessere,
            dal primo contatto fino a un'esperienza completamente personalizzata.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {levels.map((level, index) => {
            const IconComponent = level.icon;
            return (
              <div
                key={level.title}
                className={`relative rounded-3xl p-8 lg:p-10 ${level.color} border border-stone-200/50 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 ${
                  level.featured ? "ring-2 ring-[#d4c4a8]" : ""
                }`}
                data-testid={`ecosystem-level-${index + 1}`}
              >
                {level.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#2c4c3b] rounded-full">
                    <span className="text-xs text-stone-100 font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Più popolare
                    </span>
                  </div>
                )}

                {/* Level number */}
                <span className="text-6xl font-serif text-[#2c4c3b]/10 absolute top-6 right-8">
                  {level.level}
                </span>

                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-2xl ${level.iconBg} flex items-center justify-center mb-6`}
                >
                  <IconComponent className="w-6 h-6 text-[#2c4c3b]" strokeWidth={1.5} />
                </div>

                {/* Content */}
                <span className="text-xs tracking-[0.15em] uppercase text-[#8a9a86] font-medium">
                  {level.subtitle}
                </span>
                <h3 className="font-serif text-2xl text-stone-900 mt-2 mb-4">
                  {level.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-8">
                  {level.description}
                </p>

                {/* CTA */}
                {level.external ? (
                  <a
                    href={level.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#2c4c3b] font-medium text-sm group"
                    data-testid={`ecosystem-link-${index + 1}`}
                  >
                    {level.linkText}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                ) : (
                  <Link
                    to={level.link}
                    className="inline-flex items-center gap-2 text-[#2c4c3b] font-medium text-sm group"
                    data-testid={`ecosystem-link-${index + 1}`}
                  >
                    {level.linkText}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Premium Letter Image */}
        <div className="mt-16 relative rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="bg-[#2c4c3b] p-12 lg:p-16 flex flex-col justify-center">
              <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
                Esclusivo Premium
              </span>
              <h3 className="font-serif text-3xl lg:text-4xl text-stone-100 mt-4 mb-6">
                Lettere fisiche personalizzate
              </h3>
              <p className="text-stone-300 leading-relaxed mb-8">
                Nel piano Premium riceverai lettere scritte a mano, sigillate con cera,
                contenenti il tuo piano personalizzato, riflessioni e consigli esclusivi.
                Un tocco di autenticità in un mondo digitale.
              </p>
              <Link to="/pricing" data-testid="premium-letter-cta">
                <Button className="w-fit bg-stone-100 hover:bg-stone-200 text-[#2c4c3b] rounded-full px-8 py-6 text-base group">
                  Scopri Premium
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative h-[300px] lg:h-auto">
              <img
                src="https://images.unsplash.com/photo-1646568779353-b9d2b903b3e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwd2F4JTIwc2VhbCUyMGxldHRlciUyMGVudmVsb3BlJTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc3NjA3MTA3N3ww&ixlib=rb-4.1.0&q=85"
                alt="Lettera personalizzata con sigillo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
